import { readConfig } from './config';

/**
 * The panel's only way to reach the API. It is a face on admin-gated routes —
 * every rule it appears to enforce is enforced server-side (02-requirements.md
 * §Admin Dashboard), so nothing here decides anything; it carries the token and
 * turns a non-2xx into a typed error.
 *
 * **The token lives in this module**, and `auth/session.ts` mirrors it into
 * `sessionStorage` so a reload does not sign the administrator out (revised
 * 2026-08-16). Still no `localStorage` and no cookie: the session dies with the
 * tab, and the API's 8 h TTL is what ends it everywhere else.
 */
let token: string | null = null;

export const setToken = (value: string | null): void => {
  token = value;
};
export const getToken = (): string | null => token;

/** Thrown for every non-2xx. `code` is the API's snake_case error key. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /**
   * The whole parsed error body, when there was one.
   *
   * Some refusals carry the facts the screen has to show — `tag_last_on_poll`
   * names the polls that block a category's deletion — and a code alone would
   * make the panel re-fetch and re-derive what the API already worked out.
   */
  readonly details: Record<string, unknown> | null;

  constructor(
    status: number,
    code: string,
    details: Record<string, unknown> | null = null,
    message?: string,
  ) {
    super(message ?? code);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** Registered by the auth provider: a 401 means the session is over, everywhere. */
let onUnauthorized: (() => void) | null = null;
export const setUnauthorizedHandler = (handler: (() => void) | null): void => {
  onUnauthorized = handler;
};

export interface RequestOptions {
  method?: string;
  body?: unknown;
  /** FormData for the media upload; sent as-is, with no content-type header. */
  form?: FormData;
  signal?: AbortSignal;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { apiBaseUrl } = readConfig();
  const headers: Record<string, string> = {};
  if (token) headers.authorization = `Bearer ${token}`;
  if (options.body !== undefined) headers['content-type'] = 'application/json';

  const response = await fetch(`${apiBaseUrl}${path}`, {
    method: options.method ?? (options.body !== undefined || options.form ? 'POST' : 'GET'),
    headers,
    body: options.form ?? (options.body === undefined ? undefined : JSON.stringify(options.body)),
    signal: options.signal,
  });

  if (response.status === 401) {
    // The token expired or was never valid. Drop it before the caller sees the
    // error, so no later request can be made with a credential we know is dead.
    setToken(null);
    onUnauthorized?.();
  }

  if (!response.ok) {
    let code = `http_${response.status}`;
    let details: Record<string, unknown> | null = null;
    try {
      const payload = (await response.json()) as { error?: string };
      if (payload?.error) code = payload.error;
      details = payload as Record<string, unknown>;
    } catch {
      // A proxy error page is not JSON. The status code is still the truth.
    }
    throw new ApiError(response.status, code, details);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** A stored image path (`/media/x.png`) as the browser can load it. */
export function mediaUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  const { apiBaseUrl } = readConfig();
  return `${apiBaseUrl}${path.startsWith('/') ? path : `/${path}`}`;
}
